// ✅ StoreRequisitionAdd.jsx
// ✅ New Design
// ✅ React Error #185 Fixed
// ✅ Add Mode Preserved
// ✅ Edit Mode Preserved
// ✅ Existing APIs Preserved
// ✅ Vehicle / Department Preserved
// ✅ Hire / Own Preserved
// ✅ Close / Cancel Preserved
// ✅ Item Search + Grid Preserved

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import axios from "axios";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import LayoutWrapper from "../../../components/LayoutWrapper";
import HeaderBar from "../../../components/HeaderBar";

import {
  FaArrowLeft,
  FaPlus,
  FaSearch,
  FaFilter,
  FaClipboardList,
  FaBoxes,
  FaWarehouse,
  FaCar,
  FaBuilding,
  FaCheckCircle,
  FaTimesCircle,
  FaCog,
  FaInfoCircle,
  FaTrashAlt,
} from "react-icons/fa";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import { ThemeContext } from "../../../context/ThemeContext";
import getCurrentSkin from "../../../utils/getCurrentSkin";

/* =========================================================
   API
========================================================= */

const API_BASE =
  "https://nissi-india.com/api/storeReq";

/* =========================================================
   HELPERS
   IMPORTANT:
   Helpers are OUTSIDE the component so React does not create
   new function references on every render.
========================================================= */

const toStr = (value) =>
  String(value ?? "").trim();

/* ---------------------------------------------------------
   SPLIT ITEM CODE / NAME
--------------------------------------------------------- */

const splitItemCodeName = (
  combined = ""
) => {
  const trimmed =
    String(
      combined || ""
    ).trim();

  const hyphenIndex =
    trimmed.indexOf("-");

  if (
    hyphenIndex === -1
  ) {
    return {
      code: "",
      name: trimmed,
    };
  }

  return {
    code: trimmed
      .slice(
        0,
        hyphenIndex
      )
      .trim(),

    name: trimmed
      .slice(
        hyphenIndex + 1
      )
      .trim(),
  };
};

/* ---------------------------------------------------------
   NORMALIZE DATE FOR HTML DATE INPUT
--------------------------------------------------------- */

const normalizeDateForInput = (
  value
) => {
  if (!value) {
    return "";
  }

  const s =
    String(value);

  if (
    s.includes("T")
  ) {
    return s.split(
      "T"
    )[0];
  }

  if (
    /^\d{4}-\d{2}-\d{2}$/.test(
      s
    )
  ) {
    return s;
  }

  if (
    /^\d{2}\/\d{2}\/\d{4}$/.test(
      s
    )
  ) {
    const [
      dd,
      mm,
      yyyy,
    ] = s.split("/");

    return `${yyyy}-${mm}-${dd}`;
  }

  const d =
    new Date(s);

  if (
    Number.isNaN(
      d.getTime()
    )
  ) {
    return "";
  }

  const yyyy =
    d.getFullYear();

  const mm =
    String(
      d.getMonth() + 1
    ).padStart(
      2,
      "0"
    );

  const dd =
    String(
      d.getDate()
    ).padStart(
      2,
      "0"
    );

  return `${yyyy}-${mm}-${dd}`;
};

/* ---------------------------------------------------------
   NORMALIZE SELECTED ITEM
--------------------------------------------------------- */

const normalizeSelectedGridItem = (
  item = {}
) => ({
  fld_itemid:
    item.fld_itemid ||
    item.itemid ||
    item.itemId ||
    0,

  fld_itemname:
    item.fld_itemname ||
    item.itemname ||
    "",

  fld_itemcode:
    item.fld_itemcode ||
    item.itemcode ||
    "",

  fld_itemPartNo:
    item.fld_itemPartNo ||
    item.fld_itempartno ||
    item.partNo ||
    "",

  fld_branchitemid:
    item.fld_branchitemid ||
    item.branchitemid ||
    null,

  fld_uomname:
    item.fld_uomname ||
    item.itemuom ||
    "",

  fld_closing_stock:
    item.fld_closing_stock !==
      undefined &&
    item.fld_closing_stock !==
      null
      ? item.fld_closing_stock
      : "",

  fld_Qty:
    item.fld_Qty !==
      undefined &&
    item.fld_Qty !==
      null
      ? item.fld_Qty
      : item.fld_qty ||
        item.fld_RequestQty ||
        "",

  fld_duedate:
    normalizeDateForInput(
      item.fld_duedate ||
        item.fld_date ||
        ""
    ),

  fld_departmentcode:
    item.fld_departmentcode ||
    "",

  fld_vehichlecode:
    item.fld_vehichlecode ||
    item.fld_vehiclecode ||
    "",

  fld_Remarks:
    item.fld_Remarks ||
    item.fld_remarks ||
    "",

  selected:
    item.selected !==
      undefined &&
    item.selected !==
      null
      ? Boolean(
          item.selected
        )
      : true,
});

/* ---------------------------------------------------------
   UTC DATE FORMAT
--------------------------------------------------------- */

const formatDateToUTC = (
  dateString
) => {
  if (
    !dateString ||
    Number.isNaN(
      Date.parse(
        dateString
      )
    )
  ) {
    return null;
  }

  const d =
    new Date(
      `${dateString}T10:00:00`
    );

  return d
    .toISOString()
    .replace(
      ".000",
      ""
    );
};

/* =========================================================
   COMPONENT
========================================================= */

const StoreRequisitionAdd =
  () => {
    const navigate =
      useNavigate();

    const location =
      useLocation();

    /* =======================================================
       ROUTE STATE
    ======================================================= */

    const storeRequestIdFromState =
      location.state
        ?.storeRequestId ??
      null;

    const storeRequestNoFromState =
      location.state
        ?.storeRequestNo ??
      "";

    const isEditMode =
      Boolean(
        storeRequestIdFromState ||
          storeRequestNoFromState
      );

    /* =======================================================
       THEME
    ======================================================= */

    const {
      isDark,
      skin,
      setSkin,
      theme,
    } = useContext(
      ThemeContext
    );

    const currentSkin =
      useMemo(
        () =>
          getCurrentSkin(
            theme || skin
          ),
        [
          theme,
          skin,
        ]
      );

    const baseColor =
      currentSkin
        ?.baseColor ||
      "blue";

    const baseShade =
      currentSkin
        ?.baseShade ||
      "600";

    /* =======================================================
       SESSION VALUES

       These are simple string values. They do NOT depend on
       React state, therefore loading data will not recreate
       them and trigger another initialization.
    ======================================================= */

    const finYearId =
      localStorage.getItem(
        "finYearId"
      ) || "";

    const finYear =
      localStorage.getItem(
        "finYear"
      ) || "";

    const branchId =
      localStorage.getItem(
        "branchId"
      ) ||
      localStorage.getItem(
        "branchid"
      ) ||
      "";

    const branch =
      localStorage.getItem(
        "branchType"
      ) ||
      localStorage.getItem(
        "branchtype"
      ) ||
      localStorage.getItem(
        "branch"
      ) ||
      "";

    const userId =
      localStorage.getItem(
        "userId"
      ) ||
      localStorage.getItem(
        "loginuserid"
      ) ||
      "";

    /* =======================================================
       STABLE HEADERS
    ======================================================= */

    const commonHeaders =
      useMemo(
        () => ({
          "x-finyearid":
            finYearId,

          "x-finyear":
            finYear,

          "x-branchid":
            branchId,

          "x-branch":
            branch,

          "x-userid":
            userId,
        }),
        [
          finYearId,
          finYear,
          branchId,
          branch,
          userId,
        ]
      );

    /* =======================================================
       DESIGN CLASSES
    ======================================================= */

    const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }`;

    const readOnlyInputClass = `w-full px-3 py-2.5 rounded-lg border text-sm cursor-not-allowed ${
      isDark
        ? "bg-slate-900/40 border-slate-700 text-slate-400"
        : "bg-slate-100 border-slate-200 text-slate-600"
    }`;

    const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }`;

    const cardClass = `rounded-2xl shadow-xl border overflow-hidden ${
      isDark
        ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
        : `bg-white ${
            currentSkin
              ?.glass || ""
          } border-gray-200`
    }`;

    const neutralButtonClass = `inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition ${
      isDark
        ? "border-slate-600 text-slate-300 hover:bg-slate-800"
        : "border-slate-300 text-slate-700 hover:bg-slate-100"
    }`;

    const tableRowClass = `transition-colors ${
      isDark
        ? "bg-gray-800/20 hover:bg-gray-700/30"
        : "bg-white hover:bg-gray-50"
    }`;

    const tableCellClass = `px-4 py-3 text-sm whitespace-nowrap ${
      isDark
        ? "text-gray-300"
        : "text-gray-900"
    }`;

    /* =======================================================
       IMPORTANT React #185 PROTECTION

       This ref prevents the initialization effect from
       repeatedly running with the same requisition.
    ======================================================= */

    const initKeyRef =
      useRef("");

    /* =======================================================
       STATE
    ======================================================= */

    const [
      initializing,
      setInitializing,
    ] = useState(true);

    const [
      saving,
      setSaving,
    ] = useState(false);

    const [
      searchLoading,
      setSearchLoading,
    ] = useState(false);

    const [
      itemAdding,
      setItemAdding,
    ] = useState(false);

    /* -------------------------------------------------------
       MASTER
    ------------------------------------------------------- */

    const [
      currentStoreRequestId,
      setCurrentStoreRequestId,
    ] = useState(
      storeRequestIdFromState ||
        null
    );

    const [
      storeReqNo,
      setStoreReqNo,
    ] = useState(
      storeRequestNoFromState ||
        ""
    );

    const [
      date,
      setDate,
    ] = useState("");

    const [
      fromStore,
      setFromStore,
    ] = useState("");

    const [
      employee,
      setEmployee,
    ] = useState("");

    const [
      indentRef,
      setIndentRef,
    ] = useState("");

    const [
      remarks,
      setRemarks,
    ] = useState("");

    const [
      vehicleDept,
      setVehicleDept,
    ] = useState(
      "Department"
    );

    const [
      vehicleType,
      setVehicleType,
    ] = useState("");

    const [
      isClosed,
      setIsClosed,
    ] = useState(false);

    const [
      isCancelled,
      setIsCancelled,
    ] = useState(false);

    /* -------------------------------------------------------
       DROPDOWNS
    ------------------------------------------------------- */

    const [
      storeList,
      setStoreList,
    ] = useState([]);

    const [
      employeeList,
      setEmployeeList,
    ] = useState([]);

    const [
      itemCategories,
      setItemCategories,
    ] = useState([]);

    /* -------------------------------------------------------
       SEARCH
    ------------------------------------------------------- */

    const [
      itemCategory,
      setItemCategory,
    ] = useState("");

    const [
      itemName,
      setItemName,
    ] = useState("");

    const [
      itemCode,
      setItemCode,
    ] = useState("");

    const [
      searchResults,
      setSearchResults,
    ] = useState([]);

    /* -------------------------------------------------------
       SELECTED ITEMS
    ------------------------------------------------------- */

    const [
      selectedItems,
      setSelectedItems,
    ] = useState([]);

    /* =======================================================
       INITIAL LOAD FUNCTION

       IMPORTANT:
       This is stable because it only depends on stable route/
       session values and commonHeaders.
    ======================================================= */

    const loadInitialData =
      useCallback(
        async () => {
          if (
            !branchId
          ) {
            toast.error(
              "BranchId not found in localStorage"
            );

            setInitializing(
              false
            );

            return;
          }

          setInitializing(
            true
          );

          try {
            /* ===============================================
               STORES + EMPLOYEES
            =============================================== */

            const dropdownRes =
              await axios.get(
                `${API_BASE}/si_ddlfiledata`,
                {
                  params: {
                    branchid:
                      branchId,
                  },

                  headers:
                    commonHeaders,
                }
              );

            setStoreList(
              Array.isArray(
                dropdownRes
                  .data
                  ?.stores
              )
                ? dropdownRes
                    .data
                    .stores
                : []
            );

            setEmployeeList(
              Array.isArray(
                dropdownRes
                  .data
                  ?.employees
              )
                ? dropdownRes
                    .data
                    .employees
                : []
            );

            /* ===============================================
               ITEM CATEGORY
            =============================================== */

            const categoryRes =
              await axios.get(
                `${API_BASE}/si_itemcategory`,
                {
                  headers:
                    commonHeaders,
                }
              );

            setItemCategories(
              Array.isArray(
                categoryRes
                  .data
                  ?.categories
              )
                ? categoryRes
                    .data
                    .categories
                : []
            );

            /* ===============================================
               ADD MODE
            =============================================== */

            if (
              !isEditMode
            ) {
              if (!userId) {
                toast.error(
                  "UserId not found in localStorage"
                );

                return;
              }

              const reqRes =
                await axios.post(
                  `${API_BASE}/si_generateStoreRequestNo`,
                  {
                    storeIssueno:
                      "0",

                    userid:
                      userId,
                  },
                  {
                    headers:
                      commonHeaders,
                  }
                );

              setStoreReqNo(
                toStr(
                  reqRes.data
                    ?.code
                )
              );

              return;
            }

            /* ===============================================
               EDIT MODE
            =============================================== */

            const params =
              storeRequestNoFromState
                ? {
                    storeReqNo:
                      storeRequestNoFromState,
                  }
                : {
                    storeReqId:
                      storeRequestIdFromState,
                  };

            const existingRes =
              await axios.get(
                `${API_BASE}/si_existingData`,
                {
                  params,

                  headers:
                    commonHeaders,
                }
              );

            if (
              !existingRes
                .data
                ?.success
            ) {
              throw new Error(
                existingRes
                  .data
                  ?.message ||
                  "Failed to load requisition"
              );
            }

            const master =
              existingRes
                .data
                ?.master ||
              {};

            const ui =
              existingRes
                .data
                ?.ui ||
              {};

            const existingItems =
              Array.isArray(
                existingRes
                  .data
                  ?.items
              )
                ? existingRes
                    .data
                    .items
                : [];

            setCurrentStoreRequestId(
              master.storeRequestId ||
                storeRequestIdFromState ||
                null
            );

            setStoreReqNo(
              master.storeRequestNo ||
                storeRequestNoFromState ||
                ""
            );

            setDate(
              normalizeDateForInput(
                master.dateIssued
              )
            );

            setFromStore(
              master.storeId !==
                undefined &&
              master.storeId !==
                null
                ? String(
                    master.storeId
                  )
                : ""
            );

            setEmployee(
              master.employeeId !==
                undefined &&
              master.employeeId !==
                null
                ? String(
                    master.employeeId
                  )
                : ""
            );

            setIndentRef(
              master.indentRefNo ||
                ""
            );

            setRemarks(
              master.remarks ||
                ""
            );

            setIsClosed(
              Boolean(
                master.isClosed
              )
            );

            setIsCancelled(
              Boolean(
                master.isCanceled
              )
            );

            /* ===============================================
               VEHICLE / DEPARTMENT
            =============================================== */

            if (
              ui.showVehicle
            ) {
              setVehicleDept(
                "Vehicle"
              );

              if (
                ui.showVehicleHigher
              ) {
                setVehicleType(
                  "H"
                );
              } else if (
                ui.showVehicleLower
              ) {
                setVehicleType(
                  "O"
                );
              } else {
                setVehicleType(
                  ""
                );
              }
            } else {
              setVehicleDept(
                "Department"
              );

              setVehicleType(
                ""
              );
            }

            setSelectedItems(
              existingItems.map(
                (item) =>
                  normalizeSelectedGridItem(
                    {
                      ...item,
                      selected:
                        item.selected !==
                          undefined
                          ? item.selected
                          : true,
                    }
                  )
              )
            );
          } catch (error) {
            console.error(
              "Store requisition initial load error:",
              error
            );

            toast.error(
              error?.response
                ?.data
                ?.message ||
                error.message ||
                "Failed to load Store Requisition"
            );
          } finally {
            setInitializing(
              false
            );
          }
        },
        [
          branchId,
          commonHeaders,
          isEditMode,
          storeRequestIdFromState,
          storeRequestNoFromState,
          userId,
        ]
      );

    /* =======================================================
       SAFE INITIAL EFFECT

       initKeyRef prevents repeated execution if state updates
       cause a rerender.
    ======================================================= */

    useEffect(() => {
      const initKey = [
        branchId,
        isEditMode
          ? "EDIT"
          : "ADD",
        storeRequestIdFromState ||
          "",
        storeRequestNoFromState ||
          "",
        userId,
      ].join("|");

      if (
        initKeyRef.current ===
        initKey
      ) {
        return;
      }

      initKeyRef.current =
        initKey;

      loadInitialData();
    }, [
      branchId,
      isEditMode,
      storeRequestIdFromState,
      storeRequestNoFromState,
      userId,
      loadInitialData,
    ]);

    /* =======================================================
       SEARCH ITEMS
    ======================================================= */

    const handleSearch =
      async () => {
        if (
          !fromStore ||
          fromStore ===
            "Select"
        ) {
          toast.warning(
            "Please select From Store first"
          );

          return;
        }

        setSearchLoading(
          true
        );

        try {
          const response =
            await axios.get(
              `${API_BASE}/si_searchitem`,
              {
                params: {
                  branchId,

                  categoryId:
                    itemCategory ||
                    "",

                  itemCode:
                    itemCode ||
                    "",

                  itemName:
                    itemName ||
                    "",

                  store:
                    fromStore ||
                    "",
                },

                headers:
                  commonHeaders,
              }
            );

          const apiItems =
            Array.isArray(
              response.data
                ?.items
            )
              ? response.data
                  .items
              : [];

          const mapped =
            apiItems.map(
              (item) => {
                const {
                  code,
                  name,
                } =
                  splitItemCodeName(
                    item.itemname_itemcode ||
                      ""
                  );

                return {
                  fld_itemid:
                    item.itemid ||
                    item.fld_itemid ||
                    item.itemId ||
                    0,

                  fld_itemcode_raw:
                    code,

                  fld_itemcode:
                    code,

                  fld_itemname:
                    name,

                  fld_uomname:
                    item.itemuom ||
                    item.fld_uomname ||
                    "",

                  fld_Qty:
                    item.fld_qty ||
                    "",

                  fld_duedate:
                    normalizeDateForInput(
                      item.fld_date ||
                        ""
                    ),

                  fld_Remarks:
                    item.fld_remarks ||
                    "",
                };
              }
            );

          setSearchResults(
            mapped
          );

          if (
            mapped.length ===
            0
          ) {
            toast.info(
              "No items found."
            );
          } else {
            toast.success(
              `Found ${mapped.length} item(s)`
            );
          }
        } catch (error) {
          console.error(
            "Search item error:",
            error
          );

          toast.error(
            error?.response
              ?.data
              ?.message ||
              "Failed to search items"
          );
        } finally {
          setSearchLoading(
            false
          );
        }
      };

    /* =======================================================
       ADD SEARCH ITEM TO GRID
    ======================================================= */

    const handleItemSelect =
      async (
        event,
        item
      ) => {
        const checked =
          event.target.checked;

        const code =
          String(
            item.fld_itemcode_raw ||
              item.fld_itemcode ||
              ""
          );

        /* ---------------------------------------------------
           REMOVE
        --------------------------------------------------- */

        if (!checked) {
          setSelectedItems(
            (
              previous
            ) =>
              previous.filter(
                (
                  selected
                ) =>
                  String(
                    selected.fld_itemcode ||
                      ""
                  ) !==
                  code
              )
          );

          return;
        }

        /* ---------------------------------------------------
           VALIDATION
        --------------------------------------------------- */

        if (
          !vehicleDept
        ) {
          toast.warning(
            "Please select Vehicle / Department first"
          );

          return;
        }

        if (
          vehicleDept ===
            "Vehicle" &&
          !vehicleType
        ) {
          toast.warning(
            "Please select Hire or Own Vehicle"
          );

          return;
        }

        if (
          !fromStore
        ) {
          toast.warning(
            "Please select From Store"
          );

          return;
        }

        setItemAdding(
          true
        );

        try {
          const response =
            await axios.post(
              `${API_BASE}/si_fillItemsGrid`,
              {
                branchid:
                  Number(
                    branchId
                  ),

                storeid:
                  Number(
                    fromStore
                  ),

                selectid:
                  code,

                existingItems:
                  selectedItems,
              },
              {
                headers:
                  commonHeaders,
              }
            );

          const items =
            Array.isArray(
              response.data
                ?.items
            )
              ? response.data
                  .items
              : [];

          setSelectedItems(
            items.map(
              (gridItem) =>
                normalizeSelectedGridItem(
                  {
                    ...gridItem,

                    selected:
                      gridItem.selected !==
                        undefined
                        ? gridItem.selected
                        : true,
                  }
                )
            )
          );
        } catch (error) {
          console.error(
            "fillItemsGrid error:",
            error
          );

          toast.error(
            error?.response
              ?.data
              ?.message ||
              "Failed to add item to grid"
          );
        } finally {
          setItemAdding(
            false
          );
        }
      };

    /* =======================================================
       UPDATE ITEM
    ======================================================= */

    const updateSelectedItem =
      (
        index,
        field,
        value
      ) => {
        setSelectedItems(
          (
            previous
          ) =>
            previous.map(
              (
                item,
                currentIndex
              ) =>
                currentIndex ===
                index
                  ? {
                      ...item,
                      [field]:
                        value,
                    }
                  : item
            )
        );
      };

    /* =======================================================
       GRID CHECKBOX
    ======================================================= */

    const handleRowCheckboxChange =
      (
        index,
        checked
      ) => {
        setSelectedItems(
          (
            previous
          ) =>
            previous.map(
              (
                item,
                currentIndex
              ) =>
                currentIndex ===
                index
                  ? {
                      ...item,
                      selected:
                        checked,
                    }
                  : item
            )
        );
      };

    /* =======================================================
       REMOVE ITEM
    ======================================================= */

    const removeSelectedItem =
      (index) => {
        setSelectedItems(
          (
            previous
          ) =>
            previous.filter(
              (
                _,
                currentIndex
              ) =>
                currentIndex !==
                index
            )
        );
      };

    /* =======================================================
       CLOSE / CANCEL
    ======================================================= */

    const handleCloseChange =
      (checked) => {
        setIsClosed(
          checked
        );

        if (checked) {
          setIsCancelled(
            false
          );
        }
      };

    const handleCancelChange =
      (checked) => {
        setIsCancelled(
          checked
        );

        if (checked) {
          setIsClosed(
            false
          );
        }
      };

    /* =======================================================
       VALIDATE SELECTED ROWS
    ======================================================= */

    const validateSelectedItems =
      (
        items
      ) => {
        for (
          let i = 0;
          i <
          items.length;
          i += 1
        ) {
          const item =
            items[i];

          const rowNo =
            i + 1;

          if (
            !Number(
              item.fld_Qty
            ) ||
            Number(
              item.fld_Qty
            ) <= 0
          ) {
            toast.warning(
              `Please enter valid quantity for row ${rowNo}`
            );

            return false;
          }

          if (
            !item.fld_duedate
          ) {
            toast.warning(
              `Please select Due Date for row ${rowNo}`
            );

            return false;
          }

          /*
           * Existing UI marks Department Code mandatory.
           * Keep validation here.
           */

          if (
            !toStr(
              item.fld_departmentcode
            )
          ) {
            toast.warning(
              `Please enter Department Code for row ${rowNo}`
            );

            return false;
          }

          if (
            vehicleDept ===
              "Vehicle" &&
            !toStr(
              item.fld_vehichlecode
            )
          ) {
            toast.warning(
              `Please enter Vehicle Code for row ${rowNo}`
            );

            return false;
          }
        }

        return true;
      };

    /* =======================================================
       SAVE / UPDATE
    ======================================================= */

    const handleAdd =
      async () => {
        if (
          !date
        ) {
          toast.warning(
            "Please select the date"
          );

          return;
        }

        if (
          !fromStore ||
          fromStore ===
            "Select"
        ) {
          toast.warning(
            "Please select From Store"
          );

          return;
        }

        if (
          !vehicleDept
        ) {
          toast.warning(
            "Please select Vehicle or Department"
          );

          return;
        }

        if (
          vehicleDept ===
            "Vehicle" &&
          !vehicleType
        ) {
          toast.warning(
            "Please select Hire or Own Vehicle"
          );

          return;
        }

        const itemsToInsert =
          selectedItems.filter(
            (item) =>
              item.selected
          );

        if (
          itemsToInsert.length ===
          0
        ) {
          toast.warning(
            "Please select at least one item to submit."
          );

          return;
        }

        if (
          !validateSelectedItems(
            itemsToInsert
          )
        ) {
          return;
        }

        const payload = {
          branchId:
            Number(
              branchId
            ),

          userId:
            Number(
              userId
            ),

          storeId:
            Number(
              fromStore
            ),

          requestNo:
            String(
              storeReqNo ||
                ""
            ),

          requestDate:
            formatDateToUTC(
              date
            ),

          remarks:
            remarks ||
            "",

          employeeId:
            employee
              ? Number(
                  employee
                )
              : null,

          indentRefNo:
            indentRef ||
            "",

          issueFor:
            vehicleDept ===
            "Vehicle"
              ? "V"
              : "D",

          vehicleType:
            vehicleDept ===
            "Vehicle"
              ? vehicleType
              : "",

          isClosed:
            Boolean(
              isClosed
            ),

          isCancelled:
            Boolean(
              isCancelled
            ),

          storeRequestID:
            isEditMode
              ? currentStoreRequestId
              : null,

          /* preserve original behavior */

          branchWiseNo:
            isEditMode
              ? undefined
              : Math.floor(
                  1000 +
                    Math.random() *
                      9000
                ),

          items:
            itemsToInsert.map(
              (item) => ({
                itemId:
                  item.fld_itemid,

                branchItemId:
                  item.fld_branchitemid,

                requestedQty:
                  Number(
                    item.fld_Qty
                  ) || 0,

                dueDate:
                  formatDateToUTC(
                    item.fld_duedate
                  ),

                remarks:
                  item.fld_Remarks ||
                  "",

                departmentCode:
                  item.fld_departmentcode ||
                  "",

                vehicleCode:
                  vehicleDept ===
                  "Vehicle"
                    ? item.fld_vehichlecode ||
                      ""
                    : "",

                selected:
                  true,
              })
            ),
        };

        setSaving(
          true
        );

        try {
          const response =
            await axios.post(
              `${API_BASE}/si_submit`,
              payload,
              {
                headers:
                  commonHeaders,
              }
            );

          if (
            response.data
              ?.success ===
            false
          ) {
            throw new Error(
              response.data
                ?.message ||
                "Failed to save requisition"
            );
          }

          toast.success(
            isEditMode
              ? "Store Requisition updated successfully!"
              : "Store Requisition saved successfully!"
          );

          navigate(
            "/inventory/StoreRequisition_Listing"
          );
        } catch (error) {
          console.error(
            "Store requisition save error:",
            error
          );

          toast.error(
            error?.response
              ?.data
              ?.message ||
              error.message ||
              "Failed to save requisition."
          );
        } finally {
          setSaving(
            false
          );
        }
      };

    /* =======================================================
       CLEAR SEARCH
    ======================================================= */

    const clearItemSearch =
      () => {
        setItemCategory(
          ""
        );

        setItemName(
          ""
        );

        setItemCode(
          ""
        );

        setSearchResults(
          []
        );
      };

    /* =======================================================
       SELECTED COUNT
    ======================================================= */

    const selectedCount =
      useMemo(
        () =>
          selectedItems.filter(
            (item) =>
              item.selected
          ).length,
        [
          selectedItems,
        ]
      );

    /* =======================================================
       RENDER
    ======================================================= */

    return (
      <LayoutWrapper>
        {/* ===================================================
            HEADER
        =================================================== */}

        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              {isEditMode
                ? "EDIT STORE REQUISITION"
                : "STORE REQUISITION"}
            </span>
          }
          setSkin={
            setSkin
          }
          currentSkin={
            skin
          }
        />

        <div className="space-y-6 mt-6">
          {/* =================================================
              PAGE HEADER
          ================================================= */}

          <div
            className={
              cardClass
            }
          >
            <div
              className={`px-6 py-4 border-b flex flex-col lg:flex-row lg:items-center justify-between gap-4 ${
                isDark
                  ? "border-slate-700/50 bg-slate-900/40"
                  : "border-slate-200/50 bg-white/60"
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
                >
                  <FaClipboardList className="w-5 h-5 text-white" />
                </div>

                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h3
                      className={`text-lg font-bold ${
                        isDark
                          ? "text-white"
                          : "text-slate-900"
                      }`}
                    >
                      {isEditMode
                        ? "Update Store Requisition"
                        : "New Store Requisition"}
                    </h3>

                    {storeReqNo && (
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          isDark
                            ? `bg-${baseColor}-900/30 text-${baseColor}-300`
                            : `bg-${baseColor}-50 text-${baseColor}-700`
                        }`}
                      >
                        Req No:{" "}
                        {
                          storeReqNo
                        }
                      </span>
                    )}

                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        isDark
                          ? "bg-emerald-900/30 text-emerald-300"
                          : "bg-emerald-50 text-emerald-700"
                      }`}
                    >
                      {
                        selectedCount
                      }{" "}
                      selected
                    </span>

                    {(initializing ||
                      saving) && (
                      <span
                        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${
                          isDark
                            ? "bg-gray-700 text-gray-300"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        <FaCog className="animate-spin" />

                        {saving
                          ? "Saving..."
                          : "Loading..."}
                      </span>
                    )}
                  </div>

                  <p
                    className={`text-xs mt-1 ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-600"
                    }`}
                  >
                    Create or update
                    material requisitions
                    for store issue
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() =>
                  navigate(-1)
                }
                className={
                  neutralButtonClass
                }
              >
                <FaArrowLeft />

                Back
              </button>
            </div>
          </div>

          {/* =================================================
              LOADING INITIAL DATA
          ================================================= */}

          {initializing ? (
            <div
              className={
                cardClass
              }
            >
              <div className="py-20 flex flex-col items-center justify-center">
                <FaCog
                  className={`text-4xl animate-spin text-${baseColor}-500`}
                />

                <p
                  className={`mt-4 text-sm ${
                    isDark
                      ? "text-gray-400"
                      : "text-gray-600"
                  }`}
                >
                  Loading Store
                  Requisition...
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* =============================================
                  MASTER DETAILS
              ============================================= */}

              <div
                className={
                  cardClass
                }
              >
                <div
                  className={`px-6 py-4 border-b ${
                    isDark
                      ? "border-slate-700/50 bg-slate-900/40"
                      : "border-slate-200/50 bg-white/60"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
                    >
                      <FaWarehouse className="text-white" />
                    </div>

                    <div>
                      <h3
                        className={`text-lg font-bold ${
                          isDark
                            ? "text-white"
                            : "text-slate-900"
                        }`}
                      >
                        Requisition
                        Details
                      </h3>

                      <p
                        className={`text-xs ${
                          isDark
                            ? "text-slate-400"
                            : "text-slate-600"
                        }`}
                      >
                        Enter request,
                        store, employee
                        and issue details
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {/* REQUEST NO */}

                    <div>
                      <label
                        className={
                          labelClass
                        }
                      >
                        Store Req No
                      </label>

                      <input
                        type="text"
                        value={
                          storeReqNo
                        }
                        readOnly
                        className={
                          readOnlyInputClass
                        }
                      />
                    </div>

                    {/* DATE */}

                    <div>
                      <label
                        className={
                          labelClass
                        }
                      >
                        Date

                        <span className="text-red-500 ml-1">
                          *
                        </span>
                      </label>

                      <input
                        type="date"
                        value={
                          date
                        }
                        onChange={(
                          event
                        ) =>
                          setDate(
                            event
                              .target
                              .value
                          )
                        }
                        className={
                          inputClass
                        }
                      />
                    </div>

                    {/* STORE */}

                    <div>
                      <label
                        className={
                          labelClass
                        }
                      >
                        From Store

                        <span className="text-red-500 ml-1">
                          *
                        </span>
                      </label>

                      <select
                        value={
                          fromStore
                        }
                        onChange={(
                          event
                        ) => {
                          setFromStore(
                            event
                              .target
                              .value
                          );

                          /*
                           * Store changes can change
                           * available item quantities,
                           * so clear only search results.
                           */
                          setSearchResults(
                            []
                          );
                        }}
                        className={
                          inputClass
                        }
                      >
                        <option value="">
                          Select
                        </option>

                        {storeList.map(
                          (
                            store,
                            index
                          ) => (
                            <option
                              key={
                                store.fld_storeid ||
                                index
                              }
                              value={
                                store.fld_storeid
                              }
                            >
                              {
                                store.fld_storename
                              }
                            </option>
                          )
                        )}
                      </select>
                    </div>

                    {/* EMPLOYEE */}

                    <div>
                      <label
                        className={
                          labelClass
                        }
                      >
                        Employee
                      </label>

                      <select
                        value={
                          employee
                        }
                        onChange={(
                          event
                        ) =>
                          setEmployee(
                            event
                              .target
                              .value
                          )
                        }
                        className={
                          inputClass
                        }
                      >
                        <option value="">
                          Select
                        </option>

                        {employeeList.map(
                          (
                            emp,
                            index
                          ) => (
                            <option
                              key={
                                emp.fld_Employeeid ||
                                index
                              }
                              value={
                                emp.fld_Employeeid
                              }
                            >
                              {
                                emp.fld_Employee
                              }
                            </option>
                          )
                        )}
                      </select>
                    </div>

                    {/* INDENT */}

                    <div>
                      <label
                        className={
                          labelClass
                        }
                      >
                        Indent Ref
                      </label>

                      <input
                        type="text"
                        value={
                          indentRef
                        }
                        onChange={(
                          event
                        ) =>
                          setIndentRef(
                            event
                              .target
                              .value
                          )
                        }
                        className={
                          inputClass
                        }
                        placeholder="Enter indent reference"
                      />
                    </div>

                    {/* REMARKS */}

                    <div>
                      <label
                        className={
                          labelClass
                        }
                      >
                        Remarks
                      </label>

                      <textarea
                        value={
                          remarks
                        }
                        onChange={(
                          event
                        ) =>
                          setRemarks(
                            event
                              .target
                              .value
                          )
                        }
                        rows={3}
                        className={`${inputClass} resize-none`}
                        placeholder="Enter remarks"
                      />
                    </div>
                  </div>

                  {/* =========================================
                      ISSUE FOR
                  ========================================= */}

                  <div
                    className={`mt-6 p-5 rounded-xl border ${
                      isDark
                        ? "bg-slate-800/30 border-slate-700"
                        : "bg-slate-50 border-slate-200"
                    }`}
                  >
                    <label
                      className={
                        labelClass
                      }
                    >
                      Vehicle /
                      Department

                      <span className="text-red-500 ml-1">
                        *
                      </span>
                    </label>

                    <div className="flex flex-wrap gap-6 mt-3">
                      {/* DEPARTMENT */}

                      <label
                        className={`inline-flex items-center gap-2 cursor-pointer text-sm font-medium ${
                          isDark
                            ? "text-gray-200"
                            : "text-gray-700"
                        }`}
                      >
                        <input
                          type="radio"
                          checked={
                            vehicleDept ===
                            "Department"
                          }
                          onChange={() => {
                            setVehicleDept(
                              "Department"
                            );

                            setVehicleType(
                              ""
                            );
                          }}
                          className={`accent-${baseColor}-600`}
                        />

                        <FaBuilding />

                        Department
                      </label>

                      {/* VEHICLE */}

                      <label
                        className={`inline-flex items-center gap-2 cursor-pointer text-sm font-medium ${
                          isDark
                            ? "text-gray-200"
                            : "text-gray-700"
                        }`}
                      >
                        <input
                          type="radio"
                          checked={
                            vehicleDept ===
                            "Vehicle"
                          }
                          onChange={() => {
                            setVehicleDept(
                              "Vehicle"
                            );

                            setVehicleType(
                              ""
                            );
                          }}
                          className={`accent-${baseColor}-600`}
                        />

                        <FaCar />

                        Vehicle
                      </label>
                    </div>

                    {/* HIRE OWN */}

                    {vehicleDept ===
                      "Vehicle" && (
                      <div
                        className={`mt-4 ml-1 p-4 rounded-lg ${
                          isDark
                            ? "bg-slate-900/40"
                            : "bg-white"
                        }`}
                      >
                        <p
                          className={`text-xs font-semibold mb-3 ${
                            isDark
                              ? "text-gray-400"
                              : "text-gray-600"
                          }`}
                        >
                          Select Vehicle
                          Type
                        </p>

                        <div className="flex flex-wrap gap-6">
                          <label
                            className={`inline-flex items-center gap-2 cursor-pointer text-sm ${
                              isDark
                                ? "text-gray-300"
                                : "text-gray-700"
                            }`}
                          >
                            <input
                              type="radio"
                              checked={
                                vehicleType ===
                                "H"
                              }
                              onChange={() =>
                                setVehicleType(
                                  "H"
                                )
                              }
                              className={`accent-${baseColor}-600`}
                            />

                            Hire Vehicle
                          </label>

                          <label
                            className={`inline-flex items-center gap-2 cursor-pointer text-sm ${
                              isDark
                                ? "text-gray-300"
                                : "text-gray-700"
                            }`}
                          >
                            <input
                              type="radio"
                              checked={
                                vehicleType ===
                                "O"
                              }
                              onChange={() =>
                                setVehicleType(
                                  "O"
                                )
                              }
                              className={`accent-${baseColor}-600`}
                            />

                            Own Vehicle
                          </label>
                        </div>
                      </div>
                    )}

                    {/* EDIT STATUS */}

                    {isEditMode && (
                      <div
                        className={`mt-5 pt-4 border-t ${
                          isDark
                            ? "border-slate-700"
                            : "border-slate-200"
                        }`}
                      >
                        <p
                          className={`text-xs font-semibold mb-3 ${
                            isDark
                              ? "text-gray-400"
                              : "text-gray-600"
                          }`}
                        >
                          Requisition
                          Status
                        </p>

                        <div className="flex flex-wrap gap-6">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={
                                isClosed
                              }
                              onChange={(
                                event
                              ) =>
                                handleCloseChange(
                                  event
                                    .target
                                    .checked
                                )
                              }
                              disabled={
                                isCancelled
                              }
                              className={`w-5 h-5 accent-${baseColor}-600`}
                            />

                            <FaCheckCircle className="text-emerald-500" />

                            <span
                              className={`text-sm font-medium ${
                                isDark
                                  ? "text-gray-300"
                                  : "text-gray-700"
                              }`}
                            >
                              Close
                            </span>
                          </label>

                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={
                                isCancelled
                              }
                              onChange={(
                                event
                              ) =>
                                handleCancelChange(
                                  event
                                    .target
                                    .checked
                                )
                              }
                              disabled={
                                isClosed
                              }
                              className="w-5 h-5 accent-red-600"
                            />

                            <FaTimesCircle className="text-red-500" />

                            <span
                              className={`text-sm font-medium ${
                                isDark
                                  ? "text-gray-300"
                                  : "text-gray-700"
                              }`}
                            >
                              Cancel
                            </span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* =============================================
                  ITEM SEARCH
              ============================================= */}

              <div
                className={
                  cardClass
                }
              >
                <div
                  className={`px-6 py-4 border-b ${
                    isDark
                      ? "border-slate-700/50 bg-slate-900/40"
                      : "border-slate-200/50 bg-white/60"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
                    >
                      <FaFilter className="text-white" />
                    </div>

                    <div>
                      <h3
                        className={`text-lg font-bold ${
                          isDark
                            ? "text-white"
                            : "text-slate-900"
                        }`}
                      >
                        Item
                        Filtration
                        Area
                      </h3>

                      <p
                        className={`text-xs ${
                          isDark
                            ? "text-slate-400"
                            : "text-slate-600"
                        }`}
                      >
                        Search store
                        items using
                        category, item
                        name or item
                        code
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {/* CATEGORY */}

                    <div>
                      <label
                        className={
                          labelClass
                        }
                      >
                        Item Category
                      </label>

                      <select
                        value={
                          itemCategory
                        }
                        onChange={(
                          event
                        ) =>
                          setItemCategory(
                            event
                              .target
                              .value
                          )
                        }
                        className={
                          inputClass
                        }
                      >
                        <option value="">
                          All
                        </option>

                        {itemCategories.map(
                          (
                            category,
                            index
                          ) => (
                            <option
                              key={
                                category.fld_itemcategoryid ||
                                index
                              }
                              value={
                                category.fld_itemcategoryid
                              }
                            >
                              {
                                category.fld_itemcategory_name
                              }
                            </option>
                          )
                        )}
                      </select>
                    </div>

                    {/* ITEM NAME */}

                    <div>
                      <label
                        className={
                          labelClass
                        }
                      >
                        Item Name
                      </label>

                      <input
                        type="text"
                        value={
                          itemName
                        }
                        onChange={(
                          event
                        ) =>
                          setItemName(
                            event
                              .target
                              .value
                          )
                        }
                        className={
                          inputClass
                        }
                        placeholder="Enter Item Name"
                      />
                    </div>

                    {/* ITEM CODE */}

                    <div>
                      <label
                        className={
                          labelClass
                        }
                      >
                        Item Code
                      </label>

                      <input
                        type="text"
                        value={
                          itemCode
                        }
                        onChange={(
                          event
                        ) =>
                          setItemCode(
                            event
                              .target
                              .value
                          )
                        }
                        className={
                          inputClass
                        }
                        placeholder="Enter Item Code"
                      />
                    </div>
                  </div>

                  <div
                    className={`flex flex-col md:flex-row md:items-center justify-between gap-4 mt-6 pt-5 border-t ${
                      isDark
                        ? "border-slate-700"
                        : "border-slate-200"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={
                        clearItemSearch
                      }
                      className={
                        neutralButtonClass
                      }
                    >
                      <FaTimesCircle />

                      Clear Filters
                    </button>

                    <button
                      type="button"
                      onClick={
                        handleSearch
                      }
                      disabled={
                        searchLoading
                      }
                      className={`inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-60 disabled:cursor-not-allowed`}
                    >
                      {searchLoading ? (
                        <FaCog className="animate-spin" />
                      ) : (
                        <FaSearch />
                      )}

                      {searchLoading
                        ? "Searching..."
                        : "Search Items"}
                    </button>
                  </div>
                </div>
              </div>

              {/* =============================================
                  SEARCH RESULTS
              ============================================= */}

              <div
                className={
                  cardClass
                }
              >
                <div
                  className={`p-6 border-b ${
                    isDark
                      ? "border-gray-700"
                      : "border-gray-200"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                    />

                    <div>
                      <h3
                        className={`text-xl font-bold ${
                          isDark
                            ? "text-white"
                            : "text-gray-900"
                        }`}
                      >
                        Item Search
                        Results
                      </h3>

                      <p
                        className={`text-xs mt-1 ${
                          isDark
                            ? "text-gray-400"
                            : "text-gray-500"
                        }`}
                      >
                        Select an item
                        to add it to the
                        requisition grid
                      </p>
                    </div>

                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        isDark
                          ? "bg-gray-700 text-gray-300"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {
                        searchResults.length
                      }{" "}
                      records
                    </span>

                    {itemAdding && (
                      <span
                        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${
                          isDark
                            ? "bg-gray-700 text-gray-300"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        <FaCog className="animate-spin" />

                        Adding...
                      </span>
                    )}
                  </div>
                </div>

                {searchResults.length >
                0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full w-full text-sm">
                      <thead
                        className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                      >
                        <tr>
                          <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                            S.No
                          </th>

                          <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                            Item Name
                          </th>

                          <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                            Item Code
                          </th>

                          <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                            Select
                          </th>
                        </tr>
                      </thead>

                      <tbody
                        className={`divide-y ${
                          isDark
                            ? "divide-gray-700"
                            : "divide-gray-200"
                        }`}
                      >
                        {searchResults.map(
                          (
                            item,
                            index
                          ) => {
                            const code =
                              String(
                                item.fld_itemcode_raw ||
                                  item.fld_itemcode ||
                                  ""
                              );

                            const checked =
                              selectedItems.some(
                                (
                                  selected
                                ) =>
                                  String(
                                    selected.fld_itemcode ||
                                      ""
                                  ) ===
                                  code
                              );

                            return (
                              <tr
                                key={`${code}-${index}`}
                                className={
                                  tableRowClass
                                }
                              >
                                <td
                                  className={`${tableCellClass} text-center`}
                                >
                                  {index +
                                    1}
                                </td>

                                <td
                                  className={
                                    tableCellClass
                                  }
                                >
                                  <div className="flex items-center gap-2">
                                    <FaBoxes
                                      className={`text-${baseColor}-500`}
                                    />

                                    <span className="font-medium">
                                      {item.fld_itemname ||
                                        "-"}
                                    </span>
                                  </div>
                                </td>

                                <td
                                  className={
                                    tableCellClass
                                  }
                                >
                                  <span
                                    className={`inline-flex px-3 py-1 rounded-lg text-xs font-bold ${
                                      isDark
                                        ? `bg-${baseColor}-900/30 text-${baseColor}-300`
                                        : `bg-${baseColor}-50 text-${baseColor}-700`
                                    }`}
                                  >
                                    {item.fld_itemcode ||
                                      "-"}
                                  </span>
                                </td>

                                <td className="px-4 py-3 text-center">
                                  <input
                                    type="checkbox"
                                    checked={
                                      checked
                                    }
                                    disabled={
                                      itemAdding
                                    }
                                    onChange={(
                                      event
                                    ) =>
                                      handleItemSelect(
                                        event,
                                        item
                                      )
                                    }
                                    className={`w-5 h-5 accent-${baseColor}-600 cursor-pointer`}
                                  />
                                </td>
                              </tr>
                            );
                          }
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FaInfoCircle className="w-9 h-9 text-gray-400 mx-auto mb-3" />

                    <p
                      className={`text-sm ${
                        isDark
                          ? "text-gray-400"
                          : "text-gray-500"
                      }`}
                    >
                      Search for items
                      to display results.
                    </p>
                  </div>
                )}
              </div>

              {/* =============================================
                  SELECTED ITEMS
              ============================================= */}

              <div
                className={
                  cardClass
                }
              >
                <div
                  className={`p-6 border-b ${
                    isDark
                      ? "border-gray-700"
                      : "border-gray-200"
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                      />

                      <div>
                        <h3
                          className={`text-xl font-bold ${
                            isDark
                              ? "text-white"
                              : "text-gray-900"
                          }`}
                        >
                          Selected
                          Items
                        </h3>

                        <p
                          className={`text-xs mt-1 ${
                            isDark
                              ? "text-gray-400"
                              : "text-gray-500"
                          }`}
                        >
                          Enter
                          quantity, due
                          date,
                          department /
                          vehicle codes
                          and remarks
                        </p>
                      </div>

                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          isDark
                            ? "bg-gray-700 text-gray-300"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {
                          selectedItems.length
                        }{" "}
                        rows
                      </span>
                    </div>

                    <span
                      className={`px-3 py-2 rounded-lg text-xs font-semibold ${
                        isDark
                          ? "bg-emerald-900/30 text-emerald-300"
                          : "bg-emerald-50 text-emerald-700"
                      }`}
                    >
                      {
                        selectedCount
                      }{" "}
                      selected for
                      submit
                    </span>
                  </div>
                </div>

                {selectedItems.length >
                0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-[1850px] w-full text-sm">
                      <thead
                        className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                      >
                        <tr>
                          <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                            S.No
                          </th>

                          <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                            Item Name
                          </th>

                          <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                            Item Code
                          </th>

                          <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                            Part No
                          </th>

                          <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                            UOM
                          </th>

                          <th className="px-4 py-3 text-right text-xs font-bold uppercase whitespace-nowrap">
                            Available
                            Qty
                          </th>

                          <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                            Qty *
                          </th>

                          <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                            Due Date *
                          </th>

                          <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                            Department
                            Code *
                          </th>

                          {vehicleDept ===
                            "Vehicle" && (
                            <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                              Vehicle
                              Code *
                            </th>
                          )}

                          <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                            Remarks
                          </th>

                          <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                            Select
                          </th>

                          <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                            Remove
                          </th>
                        </tr>
                      </thead>

                      <tbody
                        className={`divide-y ${
                          isDark
                            ? "divide-gray-700"
                            : "divide-gray-200"
                        }`}
                      >
                        {selectedItems.map(
                          (
                            item,
                            index
                          ) => (
                            <tr
                              key={`${item.fld_itemid || "item"}-${item.fld_itemcode || ""}-${index}`}
                              className={
                                tableRowClass
                              }
                            >
                              {/* S.NO */}

                              <td
                                className={`${tableCellClass} text-center font-medium`}
                              >
                                {index +
                                  1}
                              </td>

                              {/* ITEM */}

                              <td
                                className={
                                  tableCellClass
                                }
                              >
                                <span className="font-medium">
                                  {item.fld_itemname ||
                                    "-"}
                                </span>
                              </td>

                              {/* CODE */}

                              <td
                                className={
                                  tableCellClass
                                }
                              >
                                <span
                                  className={`inline-flex px-3 py-1 rounded-lg text-xs font-bold ${
                                    isDark
                                      ? `bg-${baseColor}-900/30 text-${baseColor}-300`
                                      : `bg-${baseColor}-50 text-${baseColor}-700`
                                  }`}
                                >
                                  {item.fld_itemcode ||
                                    "-"}
                                </span>
                              </td>

                              {/* PART */}

                              <td
                                className={
                                  tableCellClass
                                }
                              >
                                {item.fld_itemPartNo ||
                                  "-"}
                              </td>

                              {/* UOM */}

                              <td className="px-4 py-3 text-center">
                                <span
                                  className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
                                    isDark
                                      ? "bg-gray-700 text-gray-200"
                                      : "bg-gray-100 text-gray-700"
                                  }`}
                                >
                                  {item.fld_uomname ||
                                    "-"}
                                </span>
                              </td>

                              {/* AVAILABLE */}

                              <td className="px-4 py-3 text-right">
                                <span
                                  className={`inline-flex min-w-[70px] justify-end px-3 py-1 rounded-lg font-semibold ${
                                    isDark
                                      ? "bg-emerald-900/30 text-emerald-300"
                                      : "bg-emerald-50 text-emerald-700"
                                  }`}
                                >
                                  {item.fld_closing_stock !==
                                    "" &&
                                  item.fld_closing_stock !==
                                    null &&
                                  item.fld_closing_stock !==
                                    undefined
                                    ? item.fld_closing_stock
                                    : "-"}
                                </span>
                              </td>

                              {/* QTY */}

                              <td className="px-3 py-3">
                                <input
                                  type="number"
                                  min="0"
                                  value={
                                    item.fld_Qty ??
                                    ""
                                  }
                                  onChange={(
                                    event
                                  ) =>
                                    updateSelectedItem(
                                      index,
                                      "fld_Qty",
                                      event
                                        .target
                                        .value
                                    )
                                  }
                                  className={`${inputClass} !w-24`}
                                />
                              </td>

                              {/* DATE */}

                              <td className="px-3 py-3">
                                <input
                                  type="date"
                                  value={
                                    item.fld_duedate ||
                                    ""
                                  }
                                  onChange={(
                                    event
                                  ) =>
                                    updateSelectedItem(
                                      index,
                                      "fld_duedate",
                                      event
                                        .target
                                        .value
                                    )
                                  }
                                  className={`${inputClass} !min-w-[150px]`}
                                />
                              </td>

                              {/* DEPT */}

                              <td className="px-3 py-3">
                                <input
                                  type="text"
                                  value={
                                    item.fld_departmentcode ||
                                    ""
                                  }
                                  onChange={(
                                    event
                                  ) =>
                                    updateSelectedItem(
                                      index,
                                      "fld_departmentcode",
                                      event
                                        .target
                                        .value
                                    )
                                  }
                                  className={`${inputClass} !min-w-[140px]`}
                                />
                              </td>

                              {/* VEHICLE */}

                              {vehicleDept ===
                                "Vehicle" && (
                                <td className="px-3 py-3">
                                  <input
                                    type="text"
                                    value={
                                      item.fld_vehichlecode ||
                                      ""
                                    }
                                    onChange={(
                                      event
                                    ) =>
                                      updateSelectedItem(
                                        index,
                                        "fld_vehichlecode",
                                        event
                                          .target
                                          .value
                                      )
                                    }
                                    className={`${inputClass} !min-w-[140px]`}
                                  />
                                </td>
                              )}

                              {/* REMARKS */}

                              <td className="px-3 py-3">
                                <input
                                  type="text"
                                  value={
                                    item.fld_Remarks ||
                                    ""
                                  }
                                  onChange={(
                                    event
                                  ) =>
                                    updateSelectedItem(
                                      index,
                                      "fld_Remarks",
                                      event
                                        .target
                                        .value
                                    )
                                  }
                                  className={`${inputClass} !min-w-[170px]`}
                                />
                              </td>

                              {/* SELECT */}

                              <td className="px-4 py-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={
                                    Boolean(
                                      item.selected
                                    )
                                  }
                                  onChange={(
                                    event
                                  ) =>
                                    handleRowCheckboxChange(
                                      index,
                                      event
                                        .target
                                        .checked
                                    )
                                  }
                                  className={`w-5 h-5 accent-${baseColor}-600 cursor-pointer`}
                                />
                              </td>

                              {/* REMOVE */}

                              <td className="px-4 py-3 text-center">
                                <button
                                  type="button"
                                  onClick={() =>
                                    removeSelectedItem(
                                      index
                                    )
                                  }
                                  className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-white bg-red-600 hover:bg-red-700 transition shadow"
                                  title="Remove Item"
                                >
                                  <FaTrashAlt
                                    size={
                                      13
                                    }
                                  />
                                </button>
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-14">
                    <div
                      className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                        isDark
                          ? "bg-gray-800"
                          : "bg-gray-100"
                      }`}
                    >
                      <FaBoxes className="w-8 h-8 text-gray-400" />
                    </div>

                    <h3
                      className={`font-semibold ${
                        isDark
                          ? "text-gray-200"
                          : "text-gray-800"
                      }`}
                    >
                      No Items
                      Selected
                    </h3>

                    <p
                      className={`text-sm mt-1 ${
                        isDark
                          ? "text-gray-400"
                          : "text-gray-500"
                      }`}
                    >
                      Search and
                      select the
                      required items
                      above.
                    </p>
                  </div>
                )}

                {/* ===========================================
                    SAVE FOOTER
                =========================================== */}

                <div
                  className={`p-6 border-t flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
                    isDark
                      ? "border-gray-700 bg-gray-900/20"
                      : "border-gray-200 bg-gray-50/50"
                  }`}
                >
                  <div
                    className={`text-sm ${
                      isDark
                        ? "text-gray-400"
                        : "text-gray-600"
                    }`}
                  >
                    {selectedCount >
                    0 ? (
                      <>
                        <span className="font-semibold">
                          {
                            selectedCount
                          }
                        </span>{" "}
                        item(s) ready
                        to{" "}
                        {isEditMode
                          ? "update"
                          : "submit"}
                      </>
                    ) : (
                      "Select at least one item before submitting."
                    )}
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          "/inventory/StoreRequisition_Listing"
                        )
                      }
                      disabled={
                        saving
                      }
                      className={
                        neutralButtonClass
                      }
                    >
                      <FaTimesCircle />

                      Cancel
                    </button>

                    <button
                      type="button"
                      onClick={
                        handleAdd
                      }
                      disabled={
                        saving
                      }
                      className={`inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-60 disabled:cursor-not-allowed`}
                    >
                      {saving ? (
                        <FaCog className="animate-spin" />
                      ) : (
                        <FaPlus />
                      )}

                      {saving
                        ? "Saving..."
                        : isEditMode
                        ? "Update Requisition"
                        : "Add Requisition"}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ===================================================
            TOAST
        =================================================== */}

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={
            isDark
              ? "dark"
              : "light"
          }
        />
      </LayoutWrapper>
    );
  };

export default StoreRequisitionAdd;